import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Loader2, Trash2, RefreshCw, AlertTriangle, ShieldCheck, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtEUR } from "../lib/format";

const LEVEL_STYLES = {
  scaduto: "bg-red-100 text-red-800 border-red-300",
  red: "bg-red-50 text-red-700 border-red-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  yellow: "bg-amber-50 text-amber-700 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  none: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

const LEVEL_LABELS = {
  scaduto: "SCADUTO",
  red: "URGENTE",
  orange: "IMMINENTE",
  yellow: "A BREVE",
  green: "OK",
  none: "—",
};

export default function Adempimenti() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get("/adempimenti/meta"),
        api.get("/adempimenti"),
      ]);
      setMeta(m.data);
      setItems(l.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ type: "polizza_rc", title: "", due_date: new Date().toISOString().slice(0, 10), amount: 0, reminder_days_before: 15, recurrence: "yearly" }); setEditOpen(true); };
  const openEdit = (it) => { setEditing({ ...it }); setEditOpen(true); };

  const save = async () => {
    if (!editing.title?.trim() || !editing.due_date) { toast.error("Titolo e scadenza obbligatori"); return; }
    try {
      if (editing.id) await api.put(`/adempimenti/${editing.id}`, editing);
      else await api.post("/adempimenti", editing);
      toast.success("Salvato");
      setEditOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/adempimenti/${id}`); await load(); };
  const renew = async (id) => { await api.post(`/adempimenti/${id}/renew`); toast.success("Scadenza rinnovata"); await load(); };

  const summary = useMemo(() => {
    const expired = items.filter((i) => i.alert_level === "scaduto").length;
    const urgent = items.filter((i) => ["red", "orange"].includes(i.alert_level)).length;
    return { expired, urgent };
  }, [items]);

  if (loading || !meta) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Studio</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Adempimenti</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Totale {items.length} adempimenti
            {summary.expired > 0 && <> · <strong className="text-red-700">{summary.expired} scaduti</strong></>}
            {summary.urgent > 0 && <> · <strong className="text-orange-700">{summary.urgent} urgenti</strong></>}
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-adem-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuovo adempimento</Button>
      </header>

      <div className="border border-neutral-200 bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
            Nessun adempimento inserito. Aggiungi la polizza RC, SOA, INARCASSA per non perdere le scadenze.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="p-3 mono-label">Tipo</th>
                <th className="p-3 mono-label">Titolo</th>
                <th className="p-3 mono-label">Scadenza</th>
                <th className="p-3 mono-label">Riferimento</th>
                <th className="p-3 mono-label text-right">Importo</th>
                <th className="p-3 mono-label">Alert</th>
                <th className="p-3 mono-label text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((it) => {
                const typeLabel = meta.types.find((t) => t.key === it.type)?.label || it.type;
                return (
                  <tr key={it.id} className="hover:bg-neutral-50" data-testid={`adem-${it.id}`}>
                    <td className="p-3 font-mono text-[10px] uppercase text-neutral-600">{typeLabel}</td>
                    <td className="p-3"><button onClick={() => openEdit(it)} className="text-left hover:underline font-medium">{it.title}</button></td>
                    <td className="p-3 font-mono text-xs">{fmtDate(it.due_date)}</td>
                    <td className="p-3 text-xs text-neutral-500">{it.external_ref || "—"}</td>
                    <td className="p-3 text-right font-mono">{it.amount ? fmtEUR(it.amount) : "—"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 font-mono text-[10px] font-semibold px-1.5 py-1 border ${LEVEL_STYLES[it.alert_level] || LEVEL_STYLES.none}`}>
                        {["red", "scaduto"].includes(it.alert_level) && <AlertTriangle className="h-2.5 w-2.5" />}
                        {LEVEL_LABELS[it.alert_level]}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => renew(it.id)} title="Rinnova scadenza" data-testid={`renew-${it.id}`} className="p-1.5 hover:bg-emerald-50 hover:text-emerald-600"><RefreshCw className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(it.id)} data-testid={`del-adem-${it.id}`} className="p-1.5 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica" : "Nuovo adempimento"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Tipo</label>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="adem-type" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Scadenza</label>
                  <Input type="date" value={editing.due_date ? editing.due_date.slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} data-testid="adem-due" />
                </div>
              </div>
              <div>
                <label className="mono-label">Titolo</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="adem-title" placeholder="es. Polizza RC Professionale 2026" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mono-label">Importo (€)</label>
                  <Input type="number" value={editing.amount || 0} onChange={(e) => setEditing({ ...editing, amount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="mono-label">Riferimento</label>
                  <Input value={editing.external_ref || ""} onChange={(e) => setEditing({ ...editing, external_ref: e.target.value })} placeholder="n° polizza / cod SOA" />
                </div>
                <div>
                  <label className="mono-label">Avviso prima</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" min="1" max="365" value={editing.reminder_days_before || 15} onChange={(e) => setEditing({ ...editing, reminder_days_before: parseInt(e.target.value) || 15 })} />
                    <span className="text-xs text-neutral-500">giorni</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="mono-label">Ricorrenza</label>
                <select value={editing.recurrence || "yearly"} onChange={(e) => setEditing({ ...editing, recurrence: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                  <option value="yearly">Annuale</option>
                  <option value="quarterly">Trimestrale</option>
                  <option value="monthly">Mensile</option>
                  <option value="none">Nessuna</option>
                </select>
              </div>
              <div>
                <label className="mono-label">Note</label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="adem-save" className="brand-bg hover:opacity-90">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
